import { useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";

import WorksheetEditor, { WorksheetEditorFormData, WorksheetModification } from "@components/forms/WorksheetEditor";
import WorksheetForm from "@components/forms/WorksheetForm";
import Modal from "@components/Modal";
import useCreateFileResource from "@hooks/mutations/useCreateFileResource";
import useOpener from "@hooks/useOpener";
import {
  GetJourneyWithPlanQuery,
  WorksheetUpdateDocument,
  WorksheetUpdateMutation,
  WorksheetUpdateMutationVariables
} from "@typing/Generated";
import { ErrorSetter, handleMutationResponse } from "@utils/formUtils";

type Props = {
  journey: GetJourneyWithPlanQuery["journey"];
  worksheetId: string;
};

const Worksheet = ({ journey, worksheetId }: Props) => {
  const [loading, setLoading] = useState(false);
  const worksheet = journey.worksheets.find(
    worksheet => worksheet.id === worksheetId || worksheet.previousIds.includes(worksheetId)
  );

  const intl = useIntl();

  const worksheetEditOpener = useOpener();
  const createFileResource = useCreateFileResource(journey.id);

  const [updateWorksheet] = useMutation<WorksheetUpdateMutation, WorksheetUpdateMutationVariables>(
    WorksheetUpdateDocument
  );

  const onSave = useCallback(
    (
      modifications: WorksheetModification[],

      setBaseErrors: (errors: string[]) => void,
      setError: ErrorSetter<WorksheetEditorFormData>
    ) =>
      updateWorksheet({
        variables: {
          id: worksheetId,
          worksheetModifications: modifications.map(modification => ({
            completed: modification.completed,
            details: modification.details,
            itemId: modification.item.id
          }))
        }
      }).then(response => {
        handleMutationResponse<WorksheetEditorFormData>({
          data: response.data?.worksheetUpdate,
          intl,
          onError: () => {
            setLoading(false);
          },
          onSuccess: () => {
            const worksheet = response.data?.worksheetUpdate?.worksheet;
            if (worksheet && !worksheet.fileResource) {
              createFileResource({
                variables: {
                  journeyId: journey.id,
                  title: worksheet.title,
                  worksheetId: worksheet.id
                }
              }).then(data => {
                if (data.data?.fileResourceCreate?.fileResource?.title) {
                  setLoading(false);
                }
              });
            }
          },
          setBaseErrors,
          setError,
          txBase: "errors.worksheet"
        });
      }),
    [createFileResource, intl, journey.id, updateWorksheet, worksheetId]
  );

  return (
    <>
      {worksheet && (
        <>
          <WorksheetEditor
            handleWorksheetEdit={worksheetEditOpener.open}
            initialWorksheet={worksheet}
            journeyId={journey.id}
            loading={loading}
            onSave={onSave}
          />

          <Modal opener={worksheetEditOpener} title={intl.formatMessage({ id: "models.worksheet.edit" })}>
            {({ modalDisplayed }) => (
              <WorksheetForm
                autofocus={modalDisplayed}
                journey={journey}
                onCancel={worksheetEditOpener.close}
                onSuccess={worksheetEditOpener.close}
                worksheet={worksheet}
              />
            )}
          </Modal>
        </>
      )}
    </>
  );
};

export default Worksheet;
